import React, { useState, useRef, useEffect, useMemo } from "react";
import "../../css/Home/searchAccordion.css";
import { ReactComponent as ArrowIcon } from '../../assets/icons/svg/arrowUnder.svg'

/*
Accordion (다중 선택)

- 역할: 모바일/아코디언 형태의 옵션 선택 UI. 부모로부터 옵션/선택값을 제어받는 controlled 컴포넌트
- 동작:
  - 옵션 클릭 시 onChange(id) 후 즉시 닫힘
  - 선택 태그에서 제거 onRemove(id)
  - isLoading일 때 로딩 뷰 노출
  - 라벨 폴백: options → fullOptions → availableOptions → globalLabelMap
  - 선택 불가 태그: hasOtherSelection && !isGlobalLoading && !isAvailable 에서만 표시
  - onToggle(opened)으로 열림/닫힘을 부모에 알림
*/

export default function SearchAccordion({
  title,
  options,
  defaultText = "선택하세요",
  onChange,
  onRemove,
  value = [],
  onOpen,
  fullOptions = [],
  availableOptions = [],
  globalLabelMap = {},
  onToggle, // open/close 상태를 부모에 알림 (신규)
  isLoading = false,
  isGlobalLoading = false,
  hasOtherSelection = false,
  isInitialLoading = false,
  rangeMode = false,
  rangeAnchorId = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const accordionRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1); // 키보드 네비게이션용 현재 포커스 옵션 인덱스

  // 🔑 input DOM 잡기
  const inputRef = useRef(null);
  const optionsRef = useRef(null); // 스크롤 컨테이너 참조

  // 아코디언 토글
  const toggleAccordion = () => {
    const next = !isOpen;
    if (next && typeof onOpen === 'function') onOpen(); // 하위호환
    if (typeof onToggle === 'function') onToggle(next);
    setIsOpen(next);
    if (!next) setSearchTerm("");
    if (!next) setActiveIndex(-1); // 닫힐 때 키보드 포커스 리셋
  };

  const handleDefaultTagActivate = (event) => { // 기본 문구 클릭 시 헤더에 전달되는 버블링을 막고 토글 실행
    event.stopPropagation(); // 부모 헤더로 클릭 이벤트가 전파되지 않도록 차단
    toggleAccordion(); // 아코디언 열림/닫힘 상태 토글
  };

  const handleDefaultTagKeyDown = (event) => { // 기본 문구 영역에서 키보드로 토글하도록 처리
    if (event.key === 'Enter' || event.key === ' ') { // Enter 또는 Space 입력만 토글 트리거
      event.preventDefault(); // Space 입력 시 페이지 스크롤 등 기본 동작을 방지
      event.stopPropagation(); // 키 이벤트가 부모로 전파되지 않도록 차단
      toggleAccordion(); // 아코디언 열림/닫힘 상태 토글
    }
  };

  const filteredOptions = useMemo(() => (options || []).filter(o => {
    if (!searchTerm) return true;
    const label = String(o.label ?? "").toLowerCase();
    return label.includes(searchTerm.toLowerCase());
  }), [options, searchTerm]);

    // 🔑 isOpen true 될 때 input에 포커스
  useEffect(() => {
    if (isOpen) {
      if (inputRef.current) {
        inputRef.current.focus();
      }
      if (optionsRef.current) {
        optionsRef.current.scrollTop = 0; // 열릴 때 스크롤 맨 위로
      }
    }
  }, [isOpen]);

  useEffect(() => { // 키보드로 이동 시 활성 옵션이 스크롤 영역 안에 들어오도록 보정
    if (!isOpen || activeIndex < 0) return;
    const container = optionsRef.current;
    if (!container) return;
    const activeEl = container.querySelector('.accordion-option.keyboard-active');
    activeEl?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  useEffect(() => { // 필터 결과 변경 시 키보드 포커스 범위 보정
    if (!isOpen) {
      setActiveIndex(-1);
      return;
    }
    setActiveIndex(prev => {
      if (!filteredOptions.length) return -1;
      if (prev < 0) return -1;
      const lastIndex = filteredOptions.length - 1;
      return Math.min(prev, lastIndex);
    });
  }, [filteredOptions, isOpen]);

  // 옵션 선택 (이미 선택된 항목은 무시)
  const handleSelect = (option) => {
    const alreadySelected = Array.isArray(value) && value.some(v => String(v) === String(option.id));
    if (!rangeMode && alreadySelected) return;
    if (onChange) onChange(option.id);
    // 선택 후 닫기 + 부모에 닫힘 알림 (구간 선택 첫 클릭은 닫지 않음)
    if (rangeMode && !rangeAnchorId) {
      setActiveIndex(-1);
      return;
    }
    setIsOpen(false);
    setActiveIndex(-1); // 선택 후 포커스 초기화
    if (typeof onToggle === 'function') onToggle(false);
  };

  const handleSearchKeyDown = (event) => { // 검색 인풋에서 방향키/엔터로 옵션 제어
    if (event.key === 'ArrowDown') {
      if (!filteredOptions.length) return;
      event.preventDefault();
      setActiveIndex(prev => {
        const nextIndex = prev + 1;
        return nextIndex >= filteredOptions.length ? filteredOptions.length - 1 : nextIndex;
      });
    } else if (event.key === 'ArrowUp') {
      if (!filteredOptions.length) return;
      event.preventDefault();
      setActiveIndex(prev => {
        const nextIndex = prev - 1;
        return nextIndex < 0 ? -1 : nextIndex;
      });
    } else if (event.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
        event.preventDefault();
        handleSelect(filteredOptions[activeIndex]);
      }
    }
  };

  const handleSearchInputChange = (event) => { // 검색어 입력 시 리스트 갱신 & 포커스 초기화
    setSearchTerm(event.target.value);
    setActiveIndex(-1);
  };

  // 외부 클릭 시 아코디언 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        accordionRef.current &&
        !accordionRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        if (typeof onToggle === 'function') onToggle(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <div className="search-accordion" ref={accordionRef}>
      <div className={`accordion-header ${isOpen ? 'open' : ''}`} onClick={toggleAccordion}>
        <div className="header-title">
          <div className="title-wrapper">
            <p className="title">
              {isInitialLoading ? <span className="option-loading-spinner" aria-label="로딩중" /> : title}
            </p>
          </div>

          {/* 선택 영역: selected-tags + input을 하나의 래퍼로 묶어서 세로 방향으로 배치 */}
          <div className="selection-stack">
            {value.length === 0 ? (
              // 선택이 없을 때: 닫힘이면 defaultText, 열림이면 input 표시
              isOpen ? (
                <div className="accordion-search" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="text"
                    ref={inputRef}
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    placeholder={`${title} 검색`}
                    className="accordion-search-input"
                    onKeyDown={handleSearchKeyDown}
                  />
                </div>
              ) : (
                <div
                  className="selected-tags default-text"
                  role="button" // 스크린 리더가 버튼으로 인식하도록 역할 지정
                  tabIndex={0} // 키보드 포커스를 받을 수 있도록 탭 인덱스 지정
                  onClick={handleDefaultTagActivate} // 클릭 시 아코디언 토글
                  onKeyDown={handleDefaultTagKeyDown} // Enter/Space 입력으로 토글
                >
                  <span className="option-name">{isInitialLoading ? "정보를 불러오고 있습니다" : defaultText}</span>
                </div>
              )
            ) : (
              <>
                <div className="selected-tags">
                  {value.map((val, idx) => {
                    const key = String(val);
                    const option = options.find(o => String(o.id) === key)
                      || (fullOptions || []).find(o => String(o.id) === key)
                      || (availableOptions || []).find(o => String(o.id) === key);
                    const label = option ? option.label : (globalLabelMap[key] ?? key);
                    const isAvailable = (availableOptions || []).some(o => String(o.id) === key);
                    const showUnavailable = hasOtherSelection && !isGlobalLoading && !isAvailable;
                    const tagClass = `selected-tag acc ${showUnavailable ? 'unavailable-selected' : ''}`;
                    return (
                      <span key={idx} className={tagClass}>
                        {label}
                        <button
                          type="button"
                          className="remove-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemove(val);
                          }}
                        >
                          ✕
                        </button>
                      </span>
                    );
                  })}
                </div>
                {isOpen && (
                  <div className="accordion-search" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      ref={inputRef}
                      value={searchTerm}
                      onChange={handleSearchInputChange}
                      placeholder={`${title} 검색`}
                      className="accordion-search-input"
                      onKeyDown={handleSearchKeyDown}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="arrow-wrapper">
          <ArrowIcon className="arrow" />
        </div>
      </div>
      <div className={`accordion-options ${isOpen ? 'open' : ''}`} onClick={(e) => e.stopPropagation()} ref={optionsRef}>
        {(isLoading || isInitialLoading) ? (
          <div className="accordion-loading">
            로딩중입니다...
          </div>
        ) : (
          <>
            {filteredOptions.length === 0 ? (
              <div className="accordion-no-result">검색 결과가 없습니다.</div>
            ) : (
              filteredOptions.map((option, idx) => {
                const key = String(option.id);
                const isSelected = (value || []).some(v => String(v) === key);
                const isActive = idx === activeIndex;
                const isAnchor = rangeMode && rangeAnchorId && String(rangeAnchorId) === key;
                const flag = (option.reservation_flg || '').toString().toUpperCase();
                const isActiveFlag = flag === 'TTA' || flag === 'DPR' || flag === 'OLR';
                const flagClass = isActiveFlag ? 'flag-active' : 'flag-non';
                const className = `accordion-option ${flagClass} ${isSelected ? 'selected-option' : ''} ${isActive ? 'keyboard-active' : ''} ${isAnchor ? 'range-anchor' : ''}`;
                const style = isSelected ? { backgroundColor: '#0066FF', color: '#FFFFFF' } : undefined;
                return (
                  <div
                    key={idx}
                    className={className}
                    onClick={() => { if (rangeMode || !isSelected) handleSelect(option); }}
                    style={style}
                  >
                    {option.label}
                  </div>
                );
              })
            )}
          </>
        )}
      </div>
    </div>
  );
}
